'use client';

import { c as _c } from "react/compiler-runtime";
import { type CryptoDepositOperation, type CryptoDepositRoute, cryptoDepositArtwork, cryptoDepositTerms, type OnrampClient } from '@morfi/onramp-sdk';
import { useCryptoDeposit } from '@morfi/onramp-sdk/react';
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useState } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { ArrowDataTransferHorizontalIcon, ArrowRight01Icon, CheckmarkCircle02Icon, CircleArrowDown02IconSolid, Wallet01Icon } from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';
import { SelectField } from '@/components/ui/select-field';
import { TOAST_IDS } from '@/config/toast-ids';
import { logger } from '@/lib/telemetry/logger';
import { dismissToast, showToast } from '@/lib/ui/toast';
import { FundingStatusHeader } from '../history/funding-status-header';
import { FundingAddress } from '../shared/funding-address';
import { useFundingStage } from '../shared/funding-chrome';
import { FundingLoading } from '../shared/funding-loading';
import { FundingQr } from '../shared/funding-qr';
import { FundingCryptoStatus } from './funding-crypto-status';
import './funding-crypto-deposit.css';
export function FundingCryptoDeposit(t0) {
  const $ = _c(30);
  const {
    client,
    active,
    operationId,
    onBack,
    onComplete
  } = t0;
  const t = useTranslations("onramp");
  const locale = useLocale();
  let t1;
  if ($[0] !== operationId) {
    t1 = operationId === undefined ? {} : {
      operationId
    };
    $[0] = operationId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== active || $[3] !== locale || $[4] !== onComplete || $[5] !== t1) {
    t2 = {
      surface: "web",
      locale,
      active,
      onComplete,
      ...t1
    };
    $[2] = active;
    $[3] = locale;
    $[4] = onComplete;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    state,
    actions
  } = useCryptoDeposit(client, t2);
  const showingInstructions = Boolean(state.operation?.instructions) && state.operation?.transfers.length === 0 && ["deposit_address", "checking_transfer"].includes(state.operation.state) && !state.showSource;
  let t3;
  if ($[7] !== t) {
    t3 = t("crypto_deposit_title");
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  useFundingStage(t3, false, showingInstructions ? actions.editSource : onBack);
  useCryptoDepositFeedback(state.checking, state.checkedWithoutTransfer);
  let t4;
  let t5;
  if ($[9] !== state.error) {
    t4 = () => {
      if (state.error) {
        logger.warn("onramp.crypto_deposit_failed", {
          code: state.error
        });
      }
    };
    t5 = [state.error];
    $[9] = state.error;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  if (!state.operation) {
    if (state.error) {
      let t6;
      if ($[12] !== actions) {
        t6 = () => {
          actions.retry();
        };
        $[12] = actions;
        $[13] = t6;
      } else {
        t6 = $[13];
      }
      let t7;
      if ($[14] !== state.busy || $[15] !== t6) {
        t7 = <CryptoUnavailable error={true} busy={state.busy} onRetry={t6} />;
        $[14] = state.busy;
        $[15] = t6;
        $[16] = t7;
      } else {
        t7 = $[16];
      }
      return t7;
    }
    let t6;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <FundingLoading />;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    return t6;
  }
  if (!["source", "deposit_address", "checking_transfer"].includes(state.operation.state)) {
    let t6;
    if ($[18] !== actions || $[19] !== state.operation) {
      t6 = <FundingCryptoStatus operation={state.operation} actions={actions} />;
      $[18] = actions;
      $[19] = state.operation;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    return t6;
  }
  if (!state.operation.instructions || state.showSource && state.operation.transfers.length === 0) {
    let t6;
    if ($[21] !== actions || $[22] !== state.busy || $[23] !== state.operation || $[24] !== state.selectedRoute) {
      t6 = <CryptoSource operation={state.operation} route={state.selectedRoute} busy={state.busy} actions={actions} />;
      $[21] = actions;
      $[22] = state.busy;
      $[23] = state.operation;
      $[24] = state.selectedRoute;
      $[25] = t6;
    } else {
      t6 = $[25];
    }
    return t6;
  }
  let t6;
  if ($[26] !== actions || $[27] !== state.checking || $[28] !== state.operation) {
    t6 = <CryptoInstructions operation={state.operation} checking={state.checking} actions={actions} />;
    $[26] = actions;
    $[27] = state.checking;
    $[28] = state.operation;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  return t6;
}
export function CryptoSource(t0) {
  const $ = _c(97);
  const {
    operation,
    route,
    busy,
    actions
  } = t0;
  const t = useTranslations("onramp");
  const quoteStatus = useTranslations("trade.status");
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  let T0;
  let t1;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== actions || $[1] !== busy || $[2] !== operation.routes || $[3] !== route || $[4] !== t) {
    t11 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const assets = unique(operation.routes, _temp);
      const networks = route ? operation.routes.filter(candidate_0 => candidate_0.asset === route.asset) : [];
      if (!route) {
        let t12;
        if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
          t12 = <CryptoUnavailable />;
          $[17] = t12;
        } else {
          t12 = $[17];
        }
        t11 = t12;
        break bb0;
      }
      let t12;
      if ($[18] !== route) {
        t12 = cryptoDepositArtwork(route);
        $[18] = route;
        $[19] = t12;
      } else {
        t12 = $[19];
      }
      const artwork = t12;
      t8 = "flex flex-col gap-4";
      const t13 = `${route.asset} to USDC on Solana`;
      const t14 = `/assets/onramp/${artwork.asset}`;
      let t15;
      if ($[20] !== t14) {
        t15 = <CryptoArtwork src={t14} compact={true} />;
        $[20] = t14;
        $[21] = t15;
      } else {
        t15 = $[21];
      }
      let t16;
      if ($[22] !== route.asset) {
        t16 = <span className="text-body font-medium">{route.asset}</span>;
        $[22] = route.asset;
        $[23] = t16;
      } else {
        t16 = $[23];
      }
      let t17;
      let t18;
      let t19;
      let t20;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = <Icon icon={ArrowRight01Icon} className="mx-auto size-icon-md text-foreground-secondary" />;
        t18 = <CryptoArtwork src="/assets/onramp/asset-usdc.png" compact={true} />;
        t19 = <span className="text-body font-medium">USDC</span>;
        t20 = <span className="text-caption text-foreground-secondary">· Solana</span>;
        $[24] = t17;
        $[25] = t18;
        $[26] = t19;
        $[27] = t20;
      } else {
        t17 = $[24];
        t18 = $[25];
        t19 = $[26];
        t20 = $[27];
      }
      if ($[28] !== t13 || $[29] !== t15 || $[30] !== t16) {
        t9 = <div className="flex items-center gap-2 px-1" role="img" aria-label={t13}>{t15}{t16}{t17}{t18}{t19}{t20}</div>;
        $[28] = t13;
        $[29] = t15;
        $[30] = t16;
        $[31] = t9;
      } else {
        t9 = $[31];
      }
      let t21;
      if ($[32] !== t) {
        t21 = t("crypto_deposit_asset");
        $[32] = t;
        $[33] = t21;
      } else {
        t21 = $[33];
      }
      let t22;
      if ($[34] !== t) {
        t22 = t("crypto_deposit_asset");
        $[34] = t;
        $[35] = t22;
      } else {
        t22 = $[35];
      }
      const t23 = route.asset;
      const t24 = `/assets/onramp/${artwork.asset}`;
      let t25;
      if ($[36] !== route.asset || $[37] !== route.assetName || $[38] !== t24) {
        t25 = <SelectValue artwork={t24} compact={true}>{route.assetName} · {route.asset}</SelectValue>;
        $[36] = route.asset;
        $[37] = route.assetName;
        $[38] = t24;
        $[39] = t25;
      } else {
        t25 = $[39];
      }
      let t26;
      if ($[40] !== actions || $[41] !== operation.routes) {
        t26 = asset => {
          const first = operation.routes.find(candidate_1 => candidate_1.asset === asset);
          if (first) {
            actions.select(first.id);
          }
        };
        $[40] = actions;
        $[41] = operation.routes;
        $[42] = t26;
      } else {
        t26 = $[42];
      }
      const t27 = assets.map(_temp2);
      if ($[43] !== busy || $[44] !== route.asset || $[45] !== t21 || $[46] !== t22 || $[47] !== t25 || $[48] !== t26 || $[49] !== t27) {
        t10 = <SelectField label={t21} aria-label={t22} value={t23} valueLabel={t25} disabled={busy} onValueChange={t26} items={t27} triggerClassName="h-13 bg-background-tertiary dark:bg-background-tertiary" />;
        $[43] = busy;
        $[44] = route.asset;
        $[45] = t21;
        $[46] = t22;
        $[47] = t25;
        $[48] = t26;
        $[49] = t27;
        $[50] = t10;
      } else {
        t10 = $[50];
      }
      T0 = SelectField;
      if ($[51] !== t) {
        t1 = t("crypto_deposit_network");
        $[51] = t;
        $[52] = t1;
      } else {
        t1 = $[52];
      }
      if ($[53] !== t) {
        t2 = t("crypto_deposit_network");
        $[53] = t;
        $[54] = t2;
      } else {
        t2 = $[54];
      }
      t3 = route.id;
      const t28 = `/assets/onramp/${artwork.network}`;
      if ($[55] !== route.networkName || $[56] !== t28) {
        t4 = <SelectValue artwork={t28} compact={true}>{route.networkName}</SelectValue>;
        $[55] = route.networkName;
        $[56] = t28;
        $[57] = t4;
      } else {
        t4 = $[57];
      }
      t5 = busy;
      t6 = actions.select;
      t7 = networks.map(_temp3);
    }
    $[0] = actions;
    $[1] = busy;
    $[2] = operation.routes;
    $[3] = route;
    $[4] = t;
    $[5] = T0;
    $[6] = t1;
    $[7] = t10;
    $[8] = t11;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    T0 = $[5];
    t1 = $[6];
    t10 = $[7];
    t11 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  if (t11 !== Symbol.for("react.early_return_sentinel")) {
    return t11;
  }
  let t12;
  if ($[58] !== T0 || $[59] !== t1 || $[60] !== t2 || $[61] !== t3 || $[62] !== t4 || $[63] !== t5 || $[64] !== t6 || $[65] !== t7) {
    t12 = <T0 label={t1} aria-label={t2} value={t3} valueLabel={t4} disabled={t5} onValueChange={t6} items={t7} triggerClassName="h-13 bg-background-tertiary dark:bg-background-tertiary" />;
    $[58] = T0;
    $[59] = t1;
    $[60] = t2;
    $[61] = t3;
    $[62] = t4;
    $[63] = t5;
    $[64] = t6;
    $[65] = t7;
    $[66] = t12;
  } else {
    t12 = $[66];
  }
  let t13;
  if ($[67] !== actions) {
    t13 = () => {
      actions.continue();
    };
    $[67] = actions;
    $[68] = t13;
  } else {
    t13 = $[68];
  }
  let t14;
  if ($[69] !== busy || $[70] !== quoteStatus || $[71] !== t) {
    t14 = busy ? quoteStatus("loading_quote") : t("continue");
    $[69] = busy;
    $[70] = quoteStatus;
    $[71] = t;
    $[72] = t14;
  } else {
    t14 = $[72];
  }
  let t15;
  if ($[73] !== busy || $[74] !== t13 || $[75] !== t14) {
    t15 = <AnimatedButton className="h-11 w-full" disabled={busy} loading={busy} onClick={t13}>{t14}</AnimatedButton>;
    $[73] = busy;
    $[74] = t13;
    $[75] = t14;
    $[76] = t15;
  } else {
    t15 = $[76];
  }
  let t16;
  if ($[77] !== t) {
    t16 = t("crypto_deposit_explainer_summary");
    $[77] = t;
    $[78] = t16;
  } else {
    t16 = $[78];
  }
  let t17;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setHowItWorksOpen(true);
    $[79] = t17;
  } else {
    t17 = $[79];
  }
  let t18;
  if ($[80] !== t) {
    t18 = t("crypto_deposit_how_it_works");
    $[80] = t;
    $[81] = t18;
  } else {
    t18 = $[81];
  }
  let t19;
  if ($[82] !== t18) {
    t19 = <button type="button" className="cursor-pointer underline underline-offset-2 hover:text-foreground" onClick={t17}>{t18}</button>;
    $[82] = t18;
    $[83] = t19;
  } else {
    t19 = $[83];
  }
  let t20;
  if ($[84] !== t16 || $[85] !== t19) {
    t20 = <p className="text-center text-caption text-foreground-secondary">{t16}{" "}{t19}</p>;
    $[84] = t16;
    $[85] = t19;
    $[86] = t20;
  } else {
    t20 = $[86];
  }
  let t21;
  if ($[87] !== howItWorksOpen) {
    t21 = <CryptoDepositHowItWorks open={howItWorksOpen} onOpenChange={setHowItWorksOpen} />;
    $[87] = howItWorksOpen;
    $[88] = t21;
  } else {
    t21 = $[88];
  }
  let t22;
  if ($[89] !== t10 || $[90] !== t12 || $[91] !== t15 || $[92] !== t20 || $[93] !== t21 || $[94] !== t8 || $[95] !== t9) {
    t22 = <div className={t8}>{t9}{t10}{t12}{t15}{t20}{t21}</div>;
    $[89] = t10;
    $[90] = t12;
    $[91] = t15;
    $[92] = t20;
    $[93] = t21;
    $[94] = t8;
    $[95] = t9;
    $[96] = t22;
  } else {
    t22 = $[96];
  }
  return t22;
}
function _temp3(candidate_3) {
  return {
    value: candidate_3.id,
    label: <SelectValue artwork={`/assets/onramp/${cryptoDepositArtwork(candidate_3).network}`}>{candidate_3.networkName}</SelectValue>
  };
}
function _temp2(candidate_2) {
  return {
    value: candidate_2.asset,
    label: <SelectValue artwork={`/assets/onramp/${cryptoDepositArtwork(candidate_2).asset}`}>{candidate_2.assetName} · {candidate_2.asset}</SelectValue>
  };
}
function _temp(candidate) {
  return candidate.asset;
}
export function CryptoInstructions(t0) {
  const $ = _c(57);
  const {
    operation,
    checking,
    actions
  } = t0;
  const t = useTranslations("onramp");
  const instructions = operation.instructions;
  const locale = useLocale();
  if (!instructions) {
    return null;
  }
  const address = instructions.address;
  const route = instructions.route;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== address || $[1] !== instructions.memo || $[2] !== locale || $[3] !== route || $[4] !== t) {
    const terms = cryptoDepositTerms(route, locale);
    let t8;
    if ($[12] !== address || $[13] !== t) {
      t8 = async function copy() {
        try {
          await navigator.clipboard.writeText(address);
          showToast({
            id: TOAST_IDS.WALLET_ADDRESS_COPIED,
            message: t("crypto_deposit_copied"),
            variant: "success"
          });
        } catch {
          logger.warn("onramp.crypto_deposit_copy_failed");
        }
      };
      $[12] = address;
      $[13] = t;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    const copy = t8;
    t1 = "flex flex-col gap-4";
    let t9;
    if ($[15] !== t) {
      t9 = t("crypto_deposit_send");
      $[15] = t;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    const t10 = `${route.asset} · ${route.networkName}`;
    if ($[17] !== t10 || $[18] !== t9) {
      t2 = <SummaryRow label={t9} value={t10} />;
      $[17] = t10;
      $[18] = t9;
      $[19] = t2;
    } else {
      t2 = $[19];
    }
    let t11;
    if ($[20] !== t) {
      t11 = t("crypto_deposit_receive");
      $[20] = t;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    let t12;
    if ($[22] !== t) {
      t12 = t("crypto_deposit_destination");
      $[22] = t;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    const t13 = `USDC · Solana · ${t12}`;
    if ($[24] !== t11 || $[25] !== t13) {
      t3 = <SummaryRow label={t11} value={t13} />;
      $[24] = t11;
      $[25] = t13;
      $[26] = t3;
    } else {
      t3 = $[26];
    }
    if ($[27] !== address) {
      t4 = <div className="funding-crypto-qr relative flex min-h-68 items-center justify-center overflow-hidden rounded-control py-6"><div className="overflow-hidden rounded-control bg-white shadow-lg"><FundingQr value={address} /></div></div>;
      $[27] = address;
      $[28] = t4;
    } else {
      t4 = $[28];
    }
    if ($[29] !== address || $[30] !== copy) {
      t5 = <FundingAddress address={address} onCopy={copy} />;
      $[29] = address;
      $[30] = copy;
      $[31] = t5;
    } else {
      t5 = $[31];
    }
    if ($[32] !== instructions.memo || $[33] !== t) {
      t6 = instructions.memo ? <SummaryRow label={t("crypto_deposit_memo")} value={instructions.memo} /> : null;
      $[32] = instructions.memo;
      $[33] = t;
      $[34] = t6;
    } else {
      t6 = $[34];
    }
    t7 = terms.minimum || terms.fee !== null ? <p className="text-center text-caption text-foreground-secondary">{terms.minimum ? terms.minimum.asset ? t("crypto_deposit_minimum", {
        amount: terms.minimum.amount,
        asset: terms.minimum.asset
      }) : t("crypto_deposit_minimum_usd", {
        amount: terms.minimum.amount
      }) : null}{terms.minimum && terms.fee !== null ? " \u2022 " : null}{terms.fee !== null ? t("crypto_deposit_fee", {
        percentage: terms.fee
      }) : null}</p> : null;
    $[0] = address;
    $[1] = instructions.memo;
    $[2] = locale;
    $[3] = route;
    $[4] = t;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
  }
  let t8;
  if ($[35] !== actions) {
    t8 = () => {
      actions.reconcile();
    };
    $[35] = actions;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== t) {
    t9 = t("crypto_deposit_transferred");
    $[37] = t;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== checking || $[40] !== t8 || $[41] !== t9) {
    t10 = <AnimatedButton className="h-11 w-full" disabled={checking} loading={checking} onClick={t8}>{t9}</AnimatedButton>;
    $[39] = checking;
    $[40] = t8;
    $[41] = t9;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== t) {
    t11 = t("crypto_deposit_warning");
    $[43] = t;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== t11) {
    t12 = <p className="text-center text-xs text-foreground-secondary">{t11}</p>;
    $[45] = t11;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== t1 || $[48] !== t10 || $[49] !== t12 || $[50] !== t2 || $[51] !== t3 || $[52] !== t4 || $[53] !== t5 || $[54] !== t6 || $[55] !== t7) {
    t13 = <div className={t1}>{t2}{t3}{t4}{t5}{t6}{t7}{t10}{t12}</div>;
    $[47] = t1;
    $[48] = t10;
    $[49] = t12;
    $[50] = t2;
    $[51] = t3;
    $[52] = t4;
    $[53] = t5;
    $[54] = t6;
    $[55] = t7;
    $[56] = t13;
  } else {
    t13 = $[56];
  }
  return t13;
}
function SummaryRow(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-caption text-foreground-secondary">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <span className="text-right text-body font-medium">{value}</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center justify-between gap-4 rounded-control bg-background-tertiary p-3">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function SelectValue(t0) {
  const $ = _c(8);
  const {
    artwork,
    compact: t1,
    children
  } = t0;
  const compact = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== artwork || $[1] !== compact) {
    t2 = <CryptoArtwork src={artwork} control={compact} />;
    $[0] = artwork;
    $[1] = compact;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children) {
    t3 = <span>{children}</span>;
    $[3] = children;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <span className="flex items-center gap-3">{t2}{t3}</span>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function CryptoDepositHowItWorks(t0) {
  const $ = _c(48);
  const {
    open,
    onOpenChange
  } = t0;
  const t = useTranslations("onramp");
  let t1;
  if ($[0] !== t) {
    t1 = t("crypto_deposit_how_title");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("crypto_deposit_got_it");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onOpenChange) {
    t3 = () => onOpenChange(false);
    $[4] = onOpenChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <InfoModal.Footer cta={t2} onClose={t3} />;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("crypto_deposit_how_wallet_title");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("crypto_deposit_how_step_wallet");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <InfoModal.IconItem icon={Wallet01Icon} title={t5} description={t6} />;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = t("crypto_deposit_how_deposit_title");
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t) {
    t9 = t("crypto_deposit_how_step_deposit");
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = <InfoModal.IconItem icon={CircleArrowDown02IconSolid} title={t8} description={t9} />;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t) {
    t11 = t("crypto_deposit_how_swap_title");
    $[23] = t;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t) {
    t12 = t("crypto_deposit_how_step_swap");
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t11 || $[28] !== t12) {
    t13 = <InfoModal.IconItem icon={ArrowDataTransferHorizontalIcon} title={t11} description={t12} />;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t) {
    t14 = t("crypto_deposit_how_delivery_title");
    $[30] = t;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t) {
    t15 = t("crypto_deposit_how_step_delivery");
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t14 || $[35] !== t15) {
    t16 = <InfoModal.IconItem icon={CheckmarkCircle02Icon} title={t14} description={t15} />;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t10 || $[38] !== t13 || $[39] !== t16 || $[40] !== t7) {
    t17 = <InfoModal.IconList>{t7}{t10}{t13}{t16}</InfoModal.IconList>;
    $[37] = t10;
    $[38] = t13;
    $[39] = t16;
    $[40] = t7;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== onOpenChange || $[43] !== open || $[44] !== t1 || $[45] !== t17 || $[46] !== t4) {
    t18 = <InfoModal open={open} onOpenChange={onOpenChange} title={t1} footer={t4}>{t17}</InfoModal>;
    $[42] = onOpenChange;
    $[43] = open;
    $[44] = t1;
    $[45] = t17;
    $[46] = t4;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  return t18;
}
function useCryptoDepositFeedback(checking, checkedWithoutTransfer) {
  const $ = _c(6);
  const t = useTranslations("onramp");
  let t0;
  let t1;
  if ($[0] !== checkedWithoutTransfer || $[1] !== checking || $[2] !== t) {
    t0 = () => {
      if (checking) {
        showToast({
          id: TOAST_IDS.ONRAMP_CRYPTO_DEPOSIT_STATUS,
          message: t("crypto_deposit_checking"),
          variant: "loading",
          duration: Number.POSITIVE_INFINITY
        });
      } else {
        if (checkedWithoutTransfer) {
          showToast({
            id: TOAST_IDS.ONRAMP_CRYPTO_DEPOSIT_STATUS,
            message: t("crypto_deposit_not_found"),
            variant: "info"
          });
        } else {
          dismissToast(TOAST_IDS.ONRAMP_CRYPTO_DEPOSIT_STATUS);
        }
      }
    };
    t1 = [checkedWithoutTransfer, checking, t];
    $[0] = checkedWithoutTransfer;
    $[1] = checking;
    $[2] = t;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useEffect(_temp5, t2);
}
function _temp5() {
  return _temp4;
}
function _temp4() {
  return dismissToast(TOAST_IDS.ONRAMP_CRYPTO_DEPOSIT_STATUS);
}
function CryptoUnavailable(t0) {
  const $ = _c(17);
  const {
    error: t1,
    busy: t2,
    onRetry
  } = t0;
  const error = t1 === undefined ? false : t1;
  const busy = t2 === undefined ? false : t2;
  const t = useTranslations("onramp");
  let t3;
  if ($[0] !== error || $[1] !== t) {
    t3 = error ? t("crypto_deposit_error_title") : t("crypto_deposit_empty_title");
    $[0] = error;
    $[1] = t;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const title = t3;
  let t4;
  if ($[3] !== error || $[4] !== t) {
    t4 = error ? t("crypto_deposit_error_description") : t("crypto_deposit_empty_description");
    $[3] = error;
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const description = t4;
  const t5 = error ? "alert" : "status";
  let t6;
  if ($[6] !== description || $[7] !== title) {
    t6 = <FundingStatusHeader artwork="offline" title={title} description={description} />;
    $[6] = description;
    $[7] = title;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== busy || $[10] !== onRetry || $[11] !== t) {
    t7 = onRetry ? <AnimatedButton className="h-11 rounded-control" disabled={busy} onClick={onRetry}>{t("retry")}</AnimatedButton> : null;
    $[9] = busy;
    $[10] = onRetry;
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t6 || $[15] !== t7) {
    t8 = <div role={t5} className="flex flex-col gap-4.5">{t6}{t7}</div>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
function CryptoArtwork(t0) {
  const $ = _c(3);
  const {
    src,
    compact: t1,
    control: t2
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const control = t2 === undefined ? false : t2;
  const t3 = `${control ? "size-6" : compact ? "size-7" : "size-8"} bg-transparent`;
  let t4;
  if ($[0] !== src || $[1] !== t3) {
    t4 = <OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" rounded="" imageClassName="object-contain" className={t3} src={src} alt="" />;
    $[0] = src;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  return t4;
}
function unique<T>(values: readonly T[], key: (value: T) => string): T[] {
  return [...new Map(values.map(value => [key(value), value])).values()];
}